import React, { useState } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { AlertTriangle, Flag, Loader2, Upload, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const reportTypes = [
  { value: 'fake_broker', label: 'Fake Broker', description: 'Broker appears fraudulent or non-existent' },
  { value: 'fake_carrier', label: 'Fake Carrier', description: 'Carrier appears fraudulent or unlicensed' },
  { value: 'suspicious_load', label: 'Suspicious Load', description: 'Load posting seems suspicious or misleading' },
  { value: 'double_brokering', label: 'Double Brokering', description: 'Load is being re-brokered without disclosure' },
  { value: 'payment_concern', label: 'Payment Concern', description: 'Issues with payment or financial fraud' },
  { value: 'identity_concern', label: 'Identity Concern', description: 'Identity theft or impersonation' },
  { value: 'scam_attempt', label: 'Scam Attempt', description: 'Active scam or phishing attempt' },
  { value: 'other', label: 'Other', description: 'Other safety or fraud concern' }
];

const ReportIssueDialog = ({ 
  open, 
  onOpenChange, 
  relatedUserId = null, 
  relatedLoadId = null,
  relatedUserName = null,
  contextType = 'general' // 'user', 'load', or 'general'
}) => {
  const [reportType, setReportType] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reportId, setReportId] = useState(null);
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);

  const handleSubmit = async () => {
    if (!reportType) {
      toast.error('Please select a report type');
      return;
    }
    if (!description || description.length < 20) {
      toast.error('Please provide a detailed description (at least 20 characters)');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/fraud-shield/reports', {
        report_type: reportType,
        description: description,
        related_user_id: relatedUserId,
        related_load_id: relatedLoadId
      });
      
      setReportId(response.data.report_id);
      setSubmitted(true);
      toast.success('Report submitted successfully');
      
    } catch (error) {
      console.error('Failed to submit report:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEvidenceUpload = async () => {
    if (!evidenceFile || !reportId) return;

    setUploadingEvidence(true);
    try {
      const formData = new FormData();
      formData.append('file', evidenceFile);
      
      await api.post(`/fraud-shield/reports/${reportId}/evidence`, formData);
      toast.success('Evidence uploaded successfully');
      setEvidenceFile(null);
    } catch (error) {
      toast.error('Failed to upload evidence');
    } finally {
      setUploadingEvidence(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset state after animation
    setTimeout(() => {
      setReportType('');
      setDescription('');
      setSubmitted(false);
      setReportId(null);
      setEvidenceFile(null);
    }, 300);
  };

  const getContextText = () => {
    if (contextType === 'user' && relatedUserName) {
      return `Reporting: ${relatedUserName}`;
    }
    if (contextType === 'load') {
      return 'Reporting suspicious load';
    }
    return 'Report a safety concern';
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#12151a] border-[#1a1d24] max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Flag className="w-5 h-5 text-[#4ADE80]" />
            Report Issue
          </DialogTitle>
          <DialogDescription>
            {getContextText()}
          </DialogDescription>
        </DialogHeader>

        {!submitted ? (
          <div className="space-y-4 py-4">
            {/* Warning Notice */}
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-400">
                  <p className="font-medium mb-1">Important</p>
                  <p className="text-yellow-400/80">
                    False reports may result in action against your account. 
                    Please only submit reports for genuine safety concerns.
                  </p>
                </div>
              </div>
            </div>

            {/* Report Type */}
            <div className="space-y-2">
              <Label className="text-white">Report Type</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger className="bg-[#0a0c10] border-[#1a1d24]">
                  <SelectValue placeholder="Select report type" />
                </SelectTrigger>
                <SelectContent>
                  {reportTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div>
                        <div className="font-medium">{type.label}</div>
                        <div className="text-xs text-gray-500">{type.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="text-white">Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Please describe the issue in detail. Include any relevant information such as dates, communication history, or specific behaviors that raised concern..."
                className="bg-[#0a0c10] border-[#1a1d24] min-h-[120px]"
              />
              <p className="text-xs text-gray-500">
                {description.length}/20 minimum characters
              </p>
            </div>
          </div>
        ) : (
          <div className="py-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Report Submitted</h3>
            <p className="text-gray-400 mb-6">
              Thank you for helping keep HaulLogix safe. Our team will review your report.
            </p>

            {/* Evidence Upload */}
            <div className="bg-[#0a0c10] rounded-lg p-4 text-left">
              <Label className="text-white mb-2 block">Add Evidence (Optional)</Label>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  onChange={(e) => setEvidenceFile(e.target.files[0])}
                  className="flex-1 text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#1a1d24] file:text-white hover:file:bg-[#252830]"
                  accept="image/*,.pdf,.doc,.docx"
                />
                <Button
                  size="sm"
                  onClick={handleEvidenceUpload}
                  disabled={!evidenceFile || uploadingEvidence}
                  className="bg-[#4ADE80] text-black hover:bg-[#22C55E]"
                >
                  {uploadingEvidence ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Upload screenshots, documents, or other evidence to support your report.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {!submitted ? (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !reportType || description.length < 20}
                className="bg-[#4ADE80] text-black hover:bg-[#22C55E]"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Report'
                )}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose} className="w-full">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReportIssueDialog;
